import React from 'react';
import { ArrowRight, Shield, Clock, Truck, Lock, Video, CheckCircle, MapPin, X } from 'lucide-react';
import SEO from '../components/SEO';

const Home = () => {
  const features = [
    {
      icon: <MapPin className="h-8 w-8 text-green-600" />,
      title: 'Easy Access',
      description: 'Located on FM 416. Easy on, easy off.'
    },
    {
      icon: <Truck className="h-8 w-8 text-green-600" />,
      title: '100% Drive-Up Units',
      description: 'Pull your vehicle right up to your unit door. No elevators, no stairs, no hassle.'
    },
    {
      icon: <Shield className="h-8 w-8 text-green-600" />,
      title: 'Concrete Driveways to Every Space',
      description: 'Pull in and load up with ease — our fully concrete driveways give you smooth, reliable access to your unit every time, rain or shine.'
    },
    {
      icon: <Video className="h-8 w-8 text-green-600" />,
      title: 'Secure & Monitored',
      description: '24/7 video surveillance and electronic gate access keep your belongings safe.'
    },
    {
      icon: <Clock className="h-8 w-8 text-green-600" />,
      title: '24-Hour Gate Access',
      description: 'Access your unit any time, day or night — 24 hours a day, 7 days a week.'
    },
    {
      icon: <Lock className="h-8 w-8 text-green-600" />,
      title: 'Online Rentals',
      description: 'Reserve your unit online, any time, from any device. Pick your size, sign your lease digitally, and you are ready to move in.'
    }
  ];

  const unitSizes = [
    {
      size: '12x15',
      sqft: '180 sq ft',
      price: '$90 / mo',
      ideal: 'Seasonal gear, small furniture, tools, or compact storage needs',
      comparable: 'Large Walk-In Closet'
    },
    {
      size: '12x30',
      sqft: '360 sq ft',
      price: '$105 / mo',
      ideal: 'Full-size boat with trailer, large RV, work truck, or contractor equipment',
      comparable: 'Double-Deep Garage Bay'
    },
    {
      size: '40x14 Outdoor',
      sqft: '560 sq ft',
      price: '$45 / mo',
      ideal: 'Oversized RVs, 5th wheels, or extra-long boat trailers needing open-air parking',
      comparable: 'Open Parking Pad'
    }
  ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SelfStorage',
    name: 'V&D Storage Streetman',
    description: 'Drive-up boat, RV, and self storage on FM 416, minutes from Richland Chambers Lake. Concrete driveways to every space. Family-owned and operated.',
    url: 'https://www.vdstoragestreetman.com',
    telephone: '(800) 768-7640',
    image: 'https://www.vdstoragestreetman.com/logo192.png',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '543 Farm to Market 416',
      addressLocality: 'Streetman',
      addressRegion: 'TX',
      postalCode: '75859',
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 31.8793,
      longitude: -96.3133
    },
    openingHoursSpecification: [
      { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'], opens: '00:00', closes: '23:59' }
    ],
    priceRange: '$',
    amenityFeature: [
      { '@type': 'LocationFeatureSpecification', name: 'Concrete Driveways', value: true },
      { '@type': 'LocationFeatureSpecification', name: 'Drive-Up Access', value: true },
      { '@type': 'LocationFeatureSpecification', name: 'Electronic Gate', value: true },
      { '@type': 'LocationFeatureSpecification', name: '24/7 Video Surveillance', value: true },
      { '@type': 'LocationFeatureSpecification', name: 'Boat & RV Storage', value: true }
    ],
    areaServed: [
      { '@type': 'City', name: 'Streetman, TX' },
      { '@type': 'City', name: 'Corsicana, TX' },
      { '@type': 'City', name: 'Kerens, TX' },
      { '@type': 'City', name: 'Dawson, TX' },
      { '@type': 'City', name: 'Eureka, TX' },
      { '@type': 'City', name: 'Richland, TX' },
      { '@type': 'City', name: 'Mildred, TX' }
    ],
    paymentAccepted: 'Cash, Visa, Mastercard, American Express, Discover'
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Boat, RV & Self Storage Near Richland Chambers Lake | V&D Storage"
        description="Drive-up boat, RV, and self storage on FM 416, minutes from Richland Chambers Lake. Concrete driveways to every space. 24/7 access. Rent online today."
        canonical="/"
        keywords="boat storage Richland Chambers Lake, RV storage Streetman TX, self storage FM 416, storage Corsicana TX, storage Kerens TX, concrete driveway storage"
        structuredData={structuredData}
      />
      {/* Hero Section */}
      <section
        className="relative h-[600px] bg-cover bg-center hero-overlay"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1600181914037-b14638c1137d)' }}
        data-testid="hero-section"
      >
        <div className="hero-content h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 animate-fadeInUp">
              Drive-Up Boat, RV & Self Storage
              <br />
              <span className="text-green-200">Near Richland Chambers Lake</span>
            </h1>
            <p className="text-xl md:text-2xl text-white mb-4 max-w-3xl animate-fadeInUp">
              Convenient storage near Richland Chambers Lake — right off FM 416.
            </p>
            <p className="text-lg md:text-xl text-green-100 mb-8 max-w-3xl animate-fadeInUp">
              100% concrete driveways. Gate to door. Rain or shine — no mud, no gravel, no excuses.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fadeInUp">
              <a
                href="https://vdstoragestreetman.storageunitsoftware.com/pages/rent"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  if (window.gtag) {
                    window.gtag('event', 'conversion_intent', {
                      'event_category': 'lead',
                      'event_label': 'hero_rates_availability',
                      'facility_name': 'VD Storage Streetman'
                    });
                  }
                }}
                className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all shadow-xl btn-primary"
                data-testid="hero-rent-button"
              >
                See Rates & Availability
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
              <a
                href="/contact"
                className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white border-2 border-white rounded-lg hover:bg-white hover:text-green-600 transition-all"
                data-testid="hero-contact-button"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              <strong>V&amp;D Storage</strong> is a locally owned, drive-up storage facility on FM 416 —
              just minutes from Richland Chambers Lake. We offer a range of unit sizes built for boats,
              RVs, work trucks, and everyday household storage.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Every unit is ground-level with drive-up access, gated entry, and 24/7 HD video
              surveillance. Whether you need a compact indoor unit or a large space for an oversized
              trailer, we have a size that fits, and getting started online is quick and hassle-free.
            </p>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-20 bg-gray-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose <span className="text-green-600">V&D Storage?</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We focus on what matters most: convenience, security, and value.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-md feature-card"
                data-testid={`feature-card-${index}`}
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Concrete Moat Section */}
      <section className="py-20 bg-white" data-testid="concrete-moat-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Headline */}
          <div className="text-center mb-16">
            <span className="inline-block bg-green-100 text-green-700 text-sm font-semibold px-4 py-1 rounded-full mb-4 uppercase tracking-wide">
              The V&amp;D Difference
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Don't Drag Your Investment<br />
              <span className="text-green-600">Through the Mud</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Most storage facilities use gravel or packed dirt. Every visit means mud, ruts, and loose rock
              near your paint. At V&amp;D Storage, every single driveway and enclosed unit is reinforced
              concrete, from the gate to your unit door.
            </p>
          </div>

          {/* Four Pillars */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            <div className="text-center p-8 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Truck className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Extra-Wide Drive Aisles</h3>
              <p className="text-gray-600">Our aisles are built wide so you can swing a large RV, a boat on a trailer, or a work truck in comfortably, without tight turns or stress.</p>
            </div>
            <div className="text-center p-8 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Precision Maneuvering</h3>
              <p className="text-gray-600">Pull straight in or back up a trailer with confidence. Smooth, level, and stable so every move is exactly what you intended.</p>
            </div>
            <div className="text-center p-8 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">No Mud, No Pests</h3>
              <p className="text-gray-600">Mud and standing water attract insects. Our surface drains cleanly and dries fast, so your loading area stays dry and pest-free every visit.</p>
            </div>
            <div className="text-center p-8 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">All-Weather Reliability</h3>
              <p className="text-gray-600">Heavy rain does not close our facility or make access dangerous. Your schedule stays on your terms, rain or shine, 365 days a year.</p>
            </div>
          </div>

          {/* Comparison Chart */}
          <div className="mb-20">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-10">How We Compare</h3>
            <div className="overflow-x-auto rounded-xl shadow-lg">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="bg-gray-100 text-gray-700 font-semibold text-left px-6 py-4 w-1/3">Feature</th>
                    <th className="bg-red-50 text-red-700 font-semibold text-center px-6 py-4 w-1/3">
                      Gravel / Dirt
                    </th>
                    <th className="bg-green-600 text-white font-semibold text-center px-6 py-4 w-1/3">
                      Reinforced Concrete<br />
                      <span className="text-xs font-normal text-green-200">(V&amp;D Storage)</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['After heavy rain', 'Muddy, rutted, impassable', 'Dry and firm, always'],
                    ['Gravel near your paint', 'Constant risk', 'Never, zero loose rock'],
                    ['Tire punctures', 'Real risk from debris', 'None'],
                    ['Backing a trailer', 'Loose surface, shifts under load', 'Solid, stable, predictable'],
                    ['All-weather reliability', 'Unreliable', 'Guaranteed'],
                    ['Dust on stored items', 'Yes, every visit', 'No'],
                  ].map(([feature, bad, good], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 font-medium text-gray-800">{feature}</td>
                      <td className="px-6 py-4 text-center text-red-600">
                        <span className="inline-flex items-center gap-1">
                          <X className="h-4 w-4 flex-shrink-0" /> {bad}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center text-green-700 font-medium">
                        <span className="inline-flex items-center gap-1">
                          <CheckCircle className="h-4 w-4 flex-shrink-0" /> {good}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Unit-Specific Copy */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-10">Concrete Access, All the Way to Your Unit</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
              <div className="bg-gray-50 rounded-xl p-8 border-t-4 border-green-600">
                <h4 className="text-lg font-bold text-gray-900 mb-2">Indoor Units</h4>
                <p className="text-sm text-green-700 font-semibold mb-3">12x15 &amp; Smaller</p>
                <p className="text-gray-600">Pull right up to your unit door on a clean concrete drive. No mud to track in, no gravel underfoot. Load and unload on a level, stable surface every single time.</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-8 border-t-4 border-green-600">
                <h4 className="text-lg font-bold text-gray-900 mb-2">Large Drive-Up Units</h4>
                <p className="text-sm text-green-700 font-semibold mb-3">12x30</p>
                <p className="text-gray-600">Back your boat or trailer straight in on solid concrete. No ruts that shift your load. No loose gravel under your tires. Wide lanes give you full control on a surface built for it.</p>
              </div>
            </div>
          </div>

          {/* Soft nudge */}
          <div className="text-center mt-14">
            <p className="text-gray-500 text-base">
              Ready to see what's available?{' '}
              <a href="/size-guide" className="text-green-600 hover:underline font-medium">
                Browse unit sizes
              </a>
            </p>
          </div>

        </div>
      </section>

            {/* Unit Sizes Preview */}
      <section className="py-20 bg-white" data-testid="unit-sizes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Popular <span className="text-green-600">Unit Sizes</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't pay for space you don't need. Find the perfect fit.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {unitSizes.map((unit, index) => (
              <div
                key={index}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
                data-testid={`unit-size-${unit.size}`}
              >

                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{unit.size}</h3>
                  <p className="text-green-600 font-semibold mb-2">{unit.sqft}</p>
                  <p className="text-sm text-gray-500 mb-2">≈ {unit.comparable}</p>
                  <p className="text-gray-600 text-sm">{unit.ideal}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a
              href="/size-guide"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-green-600 border-2 border-green-600 rounded-lg hover:bg-green-50 transition-all shadow-md mr-4"
              data-testid="size-guide-button"
            >
              See Full Size Guide
            </a>
            <a
              href="https://vdstoragestreetman.storageunitsoftware.com/pages/rent"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all shadow-md btn-primary"
              data-testid="check-prices-button"
            >
              Check Today's Prices
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Heroes Discount */}
      <section id="people-who-serve" className="scroll-mt-24 py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="h-12 w-12 mx-auto mb-5 text-green-400" />
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Honoring Those Who Serve
          </h2>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto mb-6">
            Active & veteran military, first responders, and educators give so much to our community.
            We're grateful — and we'd like to give a little back.
          </p>
          <div className="inline-block bg-green-600/20 border border-green-500/30 rounded-xl px-8 py-5 mb-6">
            <p className="text-green-400 text-3xl font-bold mb-1">10% Off Every Month</p>
            <p className="text-gray-400 text-sm">Military &bull; Police &bull; Fire &bull; EMS &bull; Educators</p>
          </div>
          <p className="text-gray-500 text-sm">Just mention your service when you rent online or call us — we'll take care of the rest.</p>
        </div>
      </section>

            {/* Location Highlight */}
      <section className="py-20 bg-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MapPin className="h-16 w-16 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Minutes from Richland Chambers Lake
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Perfect for storing boats, fishing gear, and camping equipment. Store your weekend toys
            close to the lake without paying marina prices.
          </p>
          <a
            href="/boat-rv-storage"
            className="inline-flex items-center px-8 py-3 text-lg font-semibold text-green-600 bg-white rounded-lg hover:bg-gray-100 transition-all shadow-lg"
            data-testid="boat-storage-link"
          >
            Learn About Boat & RV Storage
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Rent online in 2 minutes or call us for personalized help selecting the right unit.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://vdstoragestreetman.storageunitsoftware.com/pages/rent"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                if (window.gtag) {
                  window.gtag('event', 'conversion_intent', {
                    'event_category': 'lead',
                    'event_label': 'home_cta_rent_now',
                    'facility_name': 'VD Storage Streetman'
                  });
                }
              }}
              className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all shadow-lg btn-primary"
              data-testid="cta-rent-button"
            >
              Rent Online Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
            <a
              href="tel:8007687640"
              onClick={() => {
                if (window.gtag) {
                  window.gtag('event', 'conversion_intent', {
                    'event_category': 'lead',
                    'event_label': 'home_cta_call',
                    'facility_name': 'VD Storage Streetman'
                  });
                }
              }}
              className="inline-flex items-center px-8 py-4 text-lg font-semibold text-gray-900 bg-white rounded-lg hover:bg-gray-100 transition-all shadow-lg"
              data-testid="cta-call-button"
            >
              Call (800) 768-7640
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
